import React, { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import Papa from 'papaparse';
import {
  Select,
  Input,
  Button,
  Upload,
  Table,
  Alert,
  Space,
  Tag,
  message,
  Spin,
} from 'antd';
import { UploadOutlined, InboxOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { surveyCode } from '../../Config/surveyCode';
import { PageContainer, PageHeader, SectionCard } from '../../common';

/**
 * MakeQSetPage — 設問セット新規登録画面
 * ------------------------------------------------------------------
 * 機能企画書 F-01 / F-07 に対応する登録画面。
 * 1ページで「サーベイ情報選択 → セット情報入力 → CSV 첨부 → 登録」を完結。
 * 上から順に入力すると下のセクションが progressive reveal で現れる。
 *
 * 主な流れ:
 *   1. survey_code + type_360 を選択 → /nextSetNo 取得・既存セット表示
 *   2. set_number + company_name 入力 → set_name·set_description プレビュー
 *   3. 本人用·協力者用 CSV を첨부 → PapaParse でクライアント側パース·プレビュー
 *   4. 「登録」→ /createSet POST → 成功時 QuestionSetMgrPage へ遷移
 *
 * 認証·権限:
 *   localStorage の w_auth トークンを executorToken として送信。
 *   サーバ側で role 9 限定でガードされる。
 */

// 360度診断の form-360 候補 (config/surveyFormat.js の TYPE_360_PREFIX と同期)
const TYPE_360_OPTIONS = [
  { value: '6x6', label: '6x6 (T1)' },
  { value: '6x5', label: '6x5 (T2)' },
  { value: '5x6', label: '5x6 (T3)' },
  { value: 'cup', label: 'cup (T4)' },
  { value: '3x3x5', label: '3x3x5 (T5)' },
  { value: 'gates', label: 'gates (T6)' },
  { value: 'eleader', label: 'eleader (T7)' },
  { value: 'initiative', label: 'initiative (T8)' },
];

// type_360 → prefix の対応 (サーバと同じ)
const TYPE_360_PREFIX = {
  '6x6': 'T1',
  '6x5': 'T2',
  '5x6': 'T3',
  cup: 'T4',
  '3x3x5': 'T5',
  gates: 'T6',
  eleader: 'T7',
  initiative: 'T8',
};

// 形式別の構造定義 (config/surveyFormat.js · surveyMeta.js と同期)
// 6x5 = 大カテゴリ 6個 × 各 5問 = 30問
// 総数だけでは 6x5 と 5x6 が区別できないため構造で検証する
const SURVEY_FORMAT_STRUCTURE = {
  '6x5': { categories: 6, perCategory: 5 },
  '5x6': { categories: 5, perCategory: 6 },
  '6x6': { categories: 6, perCategory: 6 },
};
const SURVEY_FORMAT_COUNT = Object.fromEntries(
  Object.entries(SURVEY_FORMAT_STRUCTURE).map(([k, s]) => [
    k,
    s.categories * s.perCategory,
  ])
);

// 現状は 360度診断 (40010) のみ対応。今後 60020 等への拡張余地あり
const SUPPORTED_SURVEY_CODES = ['40010'];

// CSV 必須列 (utils/questionValidator.js と同期)
// choice 列は廃止 (choices テキスト数から自動算出される)
const REQUIRED_CSV_COLUMNS = [
  'page_no',
  'question_no',
  'question',
  'type',
  'sub_type',
];

const MakeQSetPage = () => {
  const history = useHistory();

  // ----- フォーム状態 -----
  const [surveyCodeSel, setSurveyCodeSel] = useState(null);
  const [type360, setType360] = useState(null);
  const [setNumber, setSetNumber] = useState('');
  const [companyName, setCompanyName] = useState('');

  // ----- データ状態 -----
  const [existingSets, setExistingSets] = useState([]);
  const [nextNoLoading, setNextNoLoading] = useState(false);

  // ----- CSV 状態 (本人用·協力者用) -----
  const [selfCsv, setSelfCsv] = useState({ rows: [], errors: [], file: null });
  const [peerCsv, setPeerCsv] = useState({ rows: [], errors: [], file: null });

  // ----- 送信状態 -----
  const [submitting, setSubmitting] = useState(false);

  // -------------------------------------------------------------------
  //  1段階: survey_code + type_360 変更時 → 既存セット取得 + 次の番号取得
  // -------------------------------------------------------------------
  useEffect(() => {
    const fetchSetInfo = async () => {
      if (!surveyCodeSel || !type360) {
        setExistingSets([]);
        setSetNumber('');
        return;
      }
      setNextNoLoading(true);
      try {
        const res = await Axios.get('/api/questionSet/nextSetNo', {
          params: { survey_code: surveyCodeSel, type_360: type360 },
        });
        if (res.data.success) {
          setSetNumber(res.data.next_no);
          // 既存セット情報は existing から構成 (詳細情報は別 API)
          const allRes = await Axios.get('/api/questionSet/');
          if (allRes.data.success) {
            const filtered = allRes.data.questionSet.filter(
              (s) =>
                s.survey_code === surveyCodeSel && s.type_360 === type360
            );
            setExistingSets(filtered);
          }
        }
      } catch (error) {
        console.log(error);
        message.error('セット情報の取得に失敗しました');
      } finally {
        setNextNoLoading(false);
      }
    };
    fetchSetInfo();
  }, [surveyCodeSel, type360]);

  // -------------------------------------------------------------------
  //  プレビュー値: set_name / set_description
  // -------------------------------------------------------------------
  const previewNames = useMemo(() => {
    const prefix = TYPE_360_PREFIX[type360];
    if (!prefix || !setNumber || !companyName) {
      return null;
    }
    return {
      selfName: `本人用${prefix}${setNumber}`,
      peerName: `協力者用${prefix}${setNumber}`,
      selfDesc: `360度診断_${type360}_本人用_${companyName}(${setNumber})`,
      peerDesc: `360度診断_${type360}_協力者用_${companyName}(${setNumber})`,
    };
  }, [type360, setNumber, companyName]);

  // 入力された番号がすでに存在するか
  // set_name から S(\d+) を抽出して数値比較する (zero-padding 差や大文字小文字差に強い)
  const isSetNumberDuplicate = useMemo(() => {
    if (!setNumber) return false;
    const inputMatch = String(setNumber).toUpperCase().match(/S(\d+)/);
    if (!inputMatch) return false;
    const targetNum = parseInt(inputMatch[1], 10);
    return existingSets.some((s) => {
      const m = String(s.set_name || '').match(/S(\d+)/);
      if (!m) return false;
      return parseInt(m[1], 10) === targetNum;
    });
  }, [setNumber, existingSets]);

  // -------------------------------------------------------------------
  //  CSV パース (PapaParse)
  //  必須列の存在チェック + sub_type 整合性チェックを行う
  // -------------------------------------------------------------------
  const parseCsv = (file, expectedSubType, onComplete) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const errors = [];
        const rows = result.data;

        // 必須列の存在チェック (ヘッダー)
        if (rows.length > 0) {
          const headers = Object.keys(rows[0]);
          const missing = REQUIRED_CSV_COLUMNS.filter(
            (col) => !headers.includes(col)
          );
          if (missing.length > 0) {
            errors.push(`必須列が不足しています: ${missing.join(', ')}`);
          }
        } else {
          errors.push('CSV にデータがありません');
        }

        // 各行のチェック
        rows.forEach((row, idx) => {
          const rowNo = idx + 1;
          // 必須フィールド
          REQUIRED_CSV_COLUMNS.forEach((col) => {
            if (
              row[col] === undefined ||
              row[col] === null ||
              String(row[col]).trim() === ''
            ) {
              errors.push(`${rowNo}行目: ${col} が空です`);
            }
          });
          // 数値型チェック
          ['page_no', 'question_no', 'type'].forEach((col) => {
            if (
              row[col] !== undefined &&
              row[col] !== '' &&
              isNaN(Number(row[col]))
            ) {
              errors.push(
                `${rowNo}行目: ${col} は数値である必要があります (現: "${row[col]}")`
              );
            }
          });
          // sub_type 整合性
          if (
            row.sub_type !== undefined &&
            String(row.sub_type).trim() !== String(expectedSubType)
          ) {
            errors.push(
              `${rowNo}行目: sub_type が ${expectedSubType} ではありません (現: ${row.sub_type})`
            );
          }
          // 選択肢検証 (type による分岐)
          // type=10 (客観式) → choice1〜choice5 のうち少なくとも 1つは入力必須
          // type=20 (記述式) → choice1〜choice5 は全て空でなければならない
          const filledChoices = [
            'choice1',
            'choice2',
            'choice3',
            'choice4',
            'choice5',
          ]
            .map((k) => row[k])
            .filter(
              (c) => c !== undefined && c !== null && String(c).trim() !== ''
            );
          if (Number(row.type) === 10) {
            if (filledChoices.length === 0) {
              errors.push(
                `${rowNo}行目: type=10 (客観式) ですが choice1〜choice5 が全て空です`
              );
            }
          } else if (Number(row.type) === 20) {
            if (filledChoices.length > 0) {
              errors.push(
                `${rowNo}行目: type=20 (記述式) は choice1〜choice5 を全て空にしてください (現: ${filledChoices.length} 個入力)`
              );
            }
          }
        });

        // F-07: 形式別の客観式問題数チェック
        // type_360 が選択済みかつ定義済み形式の場合のみ実施
        const expectedCount = SURVEY_FORMAT_COUNT[type360];
        if (type360 && expectedCount !== undefined) {
          const objectiveCount = rows.filter(
            (r) => Number(r.type) === 10
          ).length;
          if (objectiveCount !== expectedCount) {
            errors.push(
              `${type360} 形式の客観式問題は ${expectedCount} 個必要ですが、${objectiveCount} 個です`
            );
          }
        }

        // F-07 拡張: 構造チェック (カテゴリ個数 × 各カテゴリ問題数)
        // 6x5 と 5x6 のような総数同一·構造異形を区別するため
        // グルーピングフィールドは自動判定: upper1 にデータがあれば upper1、なければ category_name
        const struct = SURVEY_FORMAT_STRUCTURE[type360];
        if (type360 && struct) {
          const objective = rows.filter((r) => Number(r.type) === 10);
          const anyUpper1 = objective.some(
            (r) =>
              r.category_name_upper1 &&
              String(r.category_name_upper1).trim() !== ''
          );
          const groupField = anyUpper1 ? 'category_name_upper1' : 'category_name';
          const groupLabel = anyUpper1
            ? '大カテゴリ (category_name_upper1)'
            : 'カテゴリ (category_name)';

          // 未入力チェック
          const missing = objective.filter(
            (r) => !r[groupField] || String(r[groupField]).trim() === ''
          ).length;
          if (missing > 0) {
            errors.push(
              `${type360} 形式は${groupLabel}が必須ですが、${missing} 問で未入力です`
            );
          } else {
            // グルーピング
            const groups = new Map();
            objective.forEach((r) => {
              const cat = String(r[groupField]).trim();
              groups.set(cat, (groups.get(cat) || 0) + 1);
            });
            if (groups.size !== struct.categories) {
              errors.push(
                `${type360} 形式は${groupLabel}が ${struct.categories} 個必要ですが、${groups.size} 個です`
              );
            }
            const bad = [];
            groups.forEach((count, cat) => {
              if (count !== struct.perCategory) {
                bad.push(`「${cat}」: ${count} 問`);
              }
            });
            if (bad.length > 0) {
              errors.push(
                `${type360} 形式は各${groupLabel}に ${struct.perCategory} 問必要です。不正: ${bad.join(', ')}`
              );
            }
          }
        }

        onComplete({ rows, errors, file });
      },
      error: (err) => {
        onComplete({
          rows: [],
          errors: [`CSV のパースに失敗しました: ${err.message}`],
          file,
        });
      },
    });
  };

  // Upload コンポーネントの beforeUpload を活用してファイルを受け取る
  const handleSelfUpload = (file) => {
    parseCsv(file, '4', setSelfCsv);
    return false; // 自動アップロード抑止
  };
  const handlePeerUpload = (file) => {
    parseCsv(file, '5', setPeerCsv);
    return false;
  };

  // -------------------------------------------------------------------
  //  登録実行 → /api/questionSet/createSet
  // -------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await Axios.post('/api/questionSet/createSet', {
        survey_code: surveyCodeSel,
        type_360: type360,
        set_number: setNumber,
        company_name: companyName,
        questions_self: selfCsv.rows,
        questions_peer: peerCsv.rows,
        executorToken: localStorage.getItem('w_auth'),
      });
      if (res.data.success) {
        message.success('設問セットを登録しました');
        history.push('/questionsetmgr');
      } else {
        message.error(res.data.error || '登録に失敗しました');
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      message.error(`登録エラー: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  // 送信可能条件
  const canSubmit =
    surveyCodeSel &&
    type360 &&
    setNumber &&
    companyName &&
    !isSetNumberDuplicate &&
    selfCsv.rows.length > 0 &&
    peerCsv.rows.length > 0 &&
    selfCsv.errors.length === 0 &&
    peerCsv.errors.length === 0;

  // CSV プレビュー Table の共通カラム定義
  // choice (個数) はテキスト数から自動算出されるため表示しない
  const previewColumns = [
    { title: 'ページ', dataIndex: 'page_no', width: 60 },
    { title: '番号', dataIndex: 'question_no', width: 60 },
    { title: '大カテゴリ', dataIndex: 'category_name_upper1', width: 120 },
    { title: '小カテゴリ', dataIndex: 'category_name', width: 120 },
    { title: '問題文', dataIndex: 'question', ellipsis: true },
    { title: 'タイプ', dataIndex: 'type', width: 60 },
  ];

  // セクション間の余白
  const sectionStyle = { marginBottom: 'var(--space-6)' };

  return (
    <PageContainer>
      <PageHeader
        title="設問セット新規登録"
        subtitle="サーベイ·形式·会社情報を入力後、本人用·協力者用 CSV を添付して登録します"
        divider
      />

      {/* --- ① サーベイ情報 --- */}
      <SectionCard title="① サーベイ情報" style={sectionStyle}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <div style={labelStyle}>サーベイコード</div>
            <Select
              style={{ width: 320 }}
              placeholder="サーベイコードを選択"
              value={surveyCodeSel}
              onChange={setSurveyCodeSel}
              options={SUPPORTED_SURVEY_CODES.map((code) => ({
                value: code,
                label: `${code} - ${surveyCode[code]}`,
              }))}
            />
          </div>
          <div>
            <div style={labelStyle}>形式 (type_360)</div>
            <Select
              style={{ width: 320 }}
              placeholder="形式を選択"
              value={type360}
              onChange={setType360}
              disabled={!surveyCodeSel}
              options={TYPE_360_OPTIONS}
            />
          </div>

          {/* 既存セット一覧 (参考表示) */}
          {surveyCodeSel && type360 && (
            <div>
              <div style={labelStyle}>登録済みセット (参考)</div>
              {existingSets.length === 0 ? (
                <Tag color="default">登録なし</Tag>
              ) : (
                <Space size={[4, 4]} wrap>
                  {existingSets.map((s) => (
                    <Tag key={s._id} color="orange">
                      {s.set_name}
                    </Tag>
                  ))}
                </Space>
              )}
            </div>
          )}
        </Space>
      </SectionCard>

      {/* --- ② セット情報入力 --- */}
      {surveyCodeSel && type360 && (
        <SectionCard title="② セット情報" style={sectionStyle}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <div style={labelStyle}>
                セット番号 {nextNoLoading && <Spin size="small" />}
              </div>
              <Space align="center">
                <Input
                  style={{ width: 160 }}
                  value={setNumber}
                  onChange={(e) => setSetNumber(e.target.value.toUpperCase())}
                  placeholder="例: S06"
                  status={isSetNumberDuplicate ? 'error' : ''}
                />
                {isSetNumberDuplicate && (
                  <span
                    style={{
                      color: 'var(--red-default)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    重複している番号です
                  </span>
                )}
              </Space>
            </div>
            <div>
              <div style={labelStyle}>会社名</div>
              <Input
                style={{ width: 320 }}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="例: 株式会社〇〇"
              />
            </div>

            {/* プレビュー */}
            {previewNames && (
              <Alert
                type="info"
                showIcon
                message="生成内容プレビュー"
                description={
                  <div style={{ fontSize: 'var(--text-sm)' }}>
                    <div>本人用 set_name: <b>{previewNames.selfName}</b></div>
                    <div>本人用 set_description: {previewNames.selfDesc}</div>
                    <div style={{ marginTop: 'var(--space-2)' }}>
                      協力者用 set_name: <b>{previewNames.peerName}</b>
                    </div>
                    <div>協力者用 set_description: {previewNames.peerDesc}</div>
                  </div>
                }
              />
            )}
          </Space>
        </SectionCard>
      )}

      {/* --- ③ CSV 添付 (本人用·協力者用) --- */}
      {previewNames && (
        <SectionCard title="③ CSV 添付" style={sectionStyle}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* 本人用 */}
            <div>
              <div style={labelStyle}>本人用 CSV (sub_type:4)</div>
              <Upload.Dragger
                beforeUpload={handleSelfUpload}
                accept=".csv"
                maxCount={1}
                showUploadList={{ showRemoveIcon: false }}
                fileList={selfCsv.file ? [{ uid: '-1', name: selfCsv.file.name, status: 'done' }] : []}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: 'var(--orange-default)' }} />
                </p>
                <p>クリックまたはドラッグで CSV を添付</p>
              </Upload.Dragger>
              {selfCsv.errors.length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  message={`本人用 CSV エラー (${selfCsv.errors.length}件)`}
                  description={
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {selfCsv.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                      {selfCsv.errors.length > 10 && (
                        <li>...他 {selfCsv.errors.length - 10} 件</li>
                      )}
                    </ul>
                  }
                  style={{ marginTop: 'var(--space-2)' }}
                />
              )}
              {selfCsv.rows.length > 0 && selfCsv.errors.length === 0 && (
                <Table
                  size="small"
                  style={{ marginTop: 'var(--space-3)' }}
                  dataSource={selfCsv.rows}
                  columns={previewColumns}
                  rowKey={(_, i) => `self-${i}`}
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  title={() => `${selfCsv.rows.length} 件の問題`}
                />
              )}
            </div>

            {/* 協力者用 */}
            <div>
              <div style={labelStyle}>協力者用 CSV (sub_type:5)</div>
              <Upload.Dragger
                beforeUpload={handlePeerUpload}
                accept=".csv"
                maxCount={1}
                showUploadList={{ showRemoveIcon: false }}
                fileList={peerCsv.file ? [{ uid: '-1', name: peerCsv.file.name, status: 'done' }] : []}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: 'var(--orange-default)' }} />
                </p>
                <p>クリックまたはドラッグで CSV を添付</p>
              </Upload.Dragger>
              {peerCsv.errors.length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  message={`協力者用 CSV エラー (${peerCsv.errors.length}件)`}
                  description={
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {peerCsv.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                      {peerCsv.errors.length > 10 && (
                        <li>...他 {peerCsv.errors.length - 10} 件</li>
                      )}
                    </ul>
                  }
                  style={{ marginTop: 'var(--space-2)' }}
                />
              )}
              {peerCsv.rows.length > 0 && peerCsv.errors.length === 0 && (
                <Table
                  size="small"
                  style={{ marginTop: 'var(--space-3)' }}
                  dataSource={peerCsv.rows}
                  columns={previewColumns}
                  rowKey={(_, i) => `peer-${i}`}
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  title={() => `${peerCsv.rows.length} 件の問題`}
                />
              )}
            </div>
          </Space>
        </SectionCard>
      )}

      {/* --- ④ 登録ボタン --- */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <Button onClick={() => history.push('/questionsetmgr')}>キャンセル</Button>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          disabled={!canSubmit}
          loading={submitting}
          onClick={handleSubmit}
        >
          登録
        </Button>
      </div>
    </PageContainer>
  );
};

// セクション内ラベルの共通スタイル
const labelStyle = {
  fontSize: 'var(--text-sm)',
  color: 'var(--grey-65)',
  marginBottom: 'var(--space-1)',
  fontWeight: 'var(--font-medium)',
};

export default MakeQSetPage;
